import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AvailabilityScheduler from '@/components/shared/availibility-schedule';
import { HeaderDashboard } from '@/components/shared/header-dashboard';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-screen';
import { useGetCleanerAvailabilityRangeQuery } from '@/hooks/queries/use-get-cleaner-availability-range.query';
import { useGetCleanerAvailabilityMonthQuery } from '@/hooks/queries/use-get-cleaner-availability-month.query';
import { useUpdateCleanerAvailabilityMutation } from '@/hooks/mutations/use-update-cleaner-availability.mutation';
import { useCleanerAvailabilityStore } from '@/stores/cleaner-availability-store';
import { dateKeyFor } from '@/lib/cleaner-availability-api';
import { notifyError, notifySuccess } from '@/lib/notify';

export const Route = createFileRoute(
  '/(cleaner)/_authenticated/cleaner/availability'
)({
  component: AvailabilityPage,
});

function AvailabilityPage() {
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  // Anchor month for the month-availability query — derived from the visible
  // window's first date so dot indicators track the month the user is viewing.
  const [anchorMonth, setAnchorMonth] = useState<{
    year: number;
    month: number;
  }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  const onWeekChange = useCallback((next: { from: string; to: string }) => {
    setRange(next);
    const [y, m] = next.from.split('-').map(Number);
    setAnchorMonth({ year: y, month: m });
  }, []);

  const {
    data: rangeSlots,
    isLoading: rangeLoading,
    isError: rangeError,
  } = useGetCleanerAvailabilityRangeQuery(range?.from ?? '', range?.to ?? '');

  const { data: monthDates } = useGetCleanerAvailabilityMonthQuery(
    anchorMonth.year,
    anchorMonth.month
  );

  // Hydrate store baseline whenever fresh range data arrives.
  const setOriginalAvailability = useCleanerAvailabilityStore(
    s => s.setOriginalAvailability
  );
  useEffect(() => {
    if (!rangeSlots || !range) return;
    const byDate = new Map<string, typeof rangeSlots>();
    for (const slot of rangeSlots) {
      const key = dateKeyFor(slot.start);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key)!.push(slot);
    }
    // Ensure every visible date has an entry (even empty) so toggles work.
    const [fy, fm] = range.from.split('-').map(Number);
    const [ty, tm, td] = range.to.split('-').map(Number);
    const cursor = new Date(fy, fm - 1, 1);
    const end = new Date(ty, tm - 1, td);
    while (cursor <= end) {
      const key = dateKeyFor(cursor);
      setOriginalAvailability(key, byDate.get(key) ?? []);
      cursor.setDate(cursor.getDate() + 1);
    }
  }, [rangeSlots, range, setOriginalAvailability]);

  const hasUnsavedChanges = useCleanerAvailabilityStore(s =>
    s.hasAnyUnsavedChanges()
  );
  const getMergedModifiedAvailability = useCleanerAvailabilityStore(
    s => s.getMergedModifiedAvailability
  );
  const saveChanges = useCleanerAvailabilityStore(s => s.saveChanges);
  const discardChanges = useCleanerAvailabilityStore(s => s.discardChanges);

  const mutation = useUpdateCleanerAvailabilityMutation();

  const datesWithAvailability = useMemo(() => monthDates ?? [], [monthDates]);

  const handleSave = async () => {
    if (!hasUnsavedChanges || mutation.isPending) return;
    try {
      const changes = getMergedModifiedAvailability();
      await mutation.mutateAsync({ changes });
      saveChanges();
      notifySuccess('Availability updated successfully.');
    } catch (saveError) {
      // eslint-disable-next-line no-console
      console.error('Error saving availability changes:', saveError);
      notifyError('Failed to update availability.');
    }
  };

  const handleCancel = () => {
    discardChanges();
    window.history.back();
  };

  return (
    <div>
      <HeaderDashboard>
        <HeaderDashboard.Title>Availability Schedule</HeaderDashboard.Title>
      </HeaderDashboard>
      <div className="flex flex-col gap-6">
        {rangeError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load availability. Please refresh the page.
          </div>
        ) : rangeLoading && !rangeSlots ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        ) : null}

        <AvailabilityScheduler
          datesWithAvailability={datesWithAvailability}
          onWeekChange={onWeekChange}
        />

        <div className="flex justify-end gap-4 items-center pt-6 border-t mt-10">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="px-8"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || mutation.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-2 rounded-md transition-colors duration-200"
          >
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
